import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { darkModeRoot } from 'src/app/App.css';
import { ButtonWithIcon } from 'src/components/Buttons';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { CheckIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getAllInPriceFromListPriceForListing,
  getListPriceFromAllinPrice,
} from 'src/utils/inventoryUtils';

import { useAppContext } from '../AppContext';
import { useContent } from '../ContentContext';
import { useLocalizationContext } from '../LocalizationContext';
import { useSiteTheme } from '../SiteTheme/SiteThemeContext';
import { useMobileListingDrawerContext } from './MobileListingDrawerContext';
import { MobileListingDrawerZIndex } from './MobileListingDrawerContext.constants';

export const MobileListingPricingInputDrawer = () => {
  const { isDarkMode } = useSiteTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const { editingListing, toggleEditingListing, onSaveListingPrice } =
    useMobileListingDrawerContext();

  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();

  const uiCurrency = getUiCurrency(
    loginContext?.user?.activeAccount?.currencyCode
  );

  const setPriceText = useContent(ContentId.SetPrice);

  const allInPrice = useMemo(() => {
    return getAllInPriceFromListPriceForListing(editingListing, uiCurrency);
  }, [editingListing, uiCurrency]);

  const [inputPrice, setInputPrice] = useComputedState<number>(allInPrice ?? 0);

  const showDrawer = useMemo(() => {
    return !!editingListing;
  }, [editingListing]);

  useEffect(() => {
    if (showDrawer && inputRef.current) {
      // Delay focus to ensure drawer is fully rendered and visible
      // This is crucial for mobile browsers to trigger the keyboard
      const timeoutId = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // For iOS Safari, also try to trigger selection to ensure keyboard opens
          inputRef.current.select();
        }
      }, 300); // Delay to match typical drawer animation duration

      return () => clearTimeout(timeoutId);
    }
  }, [showDrawer]);

  const onUpdateListingPrice = useCallback(() => {
    if (!editingListing) {
      return;
    }
    const updateListPrice = getListPriceFromAllinPrice(
      inputPrice,
      editingListing
    );
    if (updateListPrice) {
      onSaveListingPrice(updateListPrice, editingListing, () =>
        toggleEditingListing(undefined)
      );
    }
  }, [editingListing, inputPrice, onSaveListingPrice, toggleEditingListing]);

  return (
    <Drawer
      opened={!!editingListing}
      onClose={() => toggleEditingListing(undefined)}
      position="bottom"
      zIndex={MobileListingDrawerZIndex.ListingPriceInput}
      withCloseButton={false}
      closeOnClickOutside
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
      styles={{
        content: { height: 'auto', minHeight: 'auto' },
        body: {
          height: 'auto',
          padding: vars.spacing.m,
          backgroundColor: vars.color.backgroundPrimary,
        },
      }}
    >
      <Stack
        width="full"
        gap="m"
        alignItems="center"
        style={{ padding: vars.spacing.sm }}
      >
        <PosTextField
          ref={inputRef}
          type="number"
          inputMode="decimal"
          step="any"
          prefixDisplay={`${setPriceText}: ${uiCurrency?.sym ?? '$'}`}
          value={inputPrice}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            const sanitized = Math.floor(v * 100) / 100;
            e.stopPropagation();
            setInputPrice(sanitized);
          }}
          // Additional mobile keyboard optimization
          enterKeyHint="done"
        />
        <ButtonWithIcon
          disabled={inputPrice == null || inputPrice === allInPrice}
          icon={<CheckIcon fill={IconsFill.textBrand} size={vars.iconSize.m} />}
          onClick={onUpdateListingPrice}
          iconOnlyMode
          iconStyle={{
            borderRadius: '50%',
            padding: vars.spacing.xs,
            border: `1px solid ${vars.color.borderActive}`,
          }}
        />
      </Stack>
    </Drawer>
  );
};
